import withRouter from "Components/Common/withRouter"
import IndustryEngagementForm from "Components/organisms/forms/industry-engagement-form"
import IndustryEngagementFields from "Components/organisms/forms/industry-engagement-form/types"
import FormPageTemplate from "Components/templates/form-page-template"
import ActionButton from "Components/atoms/buttons/action-button"
import { HelpIcon } from "Components/atoms/icons"
import useManual from "hooks/useManual"
import { IndustryEngagementValidation } from "data/industry-engagement"
import { FormikHelpers, useFormik } from "formik"
import Employee from "models/employee"
import { IndustryEngagement, IndustryEngagementStatus } from "models/industry-engagement"
import Opportunity from "models/opportunity"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { createSelector } from "reselect"
import {
    IndustryEngagementSliceState,
    setIndustryEngagement,
    setIndustryEngagementSearchFields
} from "slices/business-development/industry-engagement/reducer"
import {
    getIndustryEngagement,
    getIndustryEngagementSearchFields,
    submitEditIndustryEngagementForm
} from "slices/thunk"
import Modal from "Components/molecule/modal"
import AreYouSureButtons from "Components/molecule/are-you-sure-buttons"
import { NavigateFunction, useSearchParams } from "react-router-dom"
import { toast } from "react-toastify"


const selectIndustryEngagement = createSelector(
    (state: any) => state.IndustryEngagement,
    ({
        statuses,
        employees,
        opportunities,
        industryEngagements
    }: IndustryEngagementSliceState) => ({
        statuses,
        employees,
        opportunities,
        industryEngagements
    })
)

const EditIndustryEngagementPage = (props: any) => {
    document.title = "Edit Industry Engagement | IbexIQ";
    const { handleOpenManual } = useManual("business-development/industry-engagement");
    const [showAreYouSureModal, setShowAreYouSureModal] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [focusedEngagement, setFocusedEngagement] = useState<IndustryEngagement | null>(null)
    const dispatch: any = useDispatch()

    const [searchParams] = useSearchParams()

    const { statuses, employees, opportunities, industryEngagements } = useSelector(selectIndustryEngagement)

    const initializePage = async (industryEngagementId: string, navigate: NavigateFunction) => {
        if (statuses.length === 0 || employees.length === 0 || opportunities.length === 0) {
            const res = await getIndustryEngagementSearchFields()
            if (!res) return;
            dispatch(setIndustryEngagementSearchFields(res))
        }

        const engagement = await getIndustryEngagement(industryEngagementId)
        if (!engagement) {
            toast.error("Something went wrong. Could not find Industry Engagement")
            navigate(-1)
            return;
        }

        setFocusedEngagement(engagement)
        setIsLoading(false)
    }

    useEffect(() => {
        const industryEngagementId = searchParams.get("industryEngagementId")

        if (!industryEngagementId) {
            toast.error("Something went wrong. Industry Engagement Id not found")
            props.router.navigate(-1)
        } else {
            initializePage(industryEngagementId, props.router.navigate)
        }
    }, [searchParams, props.router.navigate])

    const handleSubmitForm = (_vals: IndustryEngagementFields, helpers: FormikHelpers<IndustryEngagementFields>) => {
        helpers.setSubmitting(false)
        setShowAreYouSureModal(true)
    }

    const initialValues: IndustryEngagementFields = useMemo(() => ({
        name: focusedEngagement?.name ?? '',
        date: focusedEngagement?.date?.toString().split("T")[0] ?? '',
        description: focusedEngagement?.description ?? '',
        statusId: focusedEngagement?.statusId ?? '',
        attendeeIds: focusedEngagement?.attendees?.map(a => a.employee!.id) ?? [],
        opportunityIds: focusedEngagement?.opportunities?.map(o => o.id) ?? []
    }), [focusedEngagement])

    const {
        values,
        errors,
        touched,
        handleSubmit,
        handleBlur,
        handleChange,
        isValid,
        setSubmitting,
        isSubmitting,
        handleReset,
        setFieldValue
    } = useFormik({
        onSubmit: handleSubmitForm,
        enableReinitialize: true,
        initialValues,
        validationSchema: IndustryEngagementValidation
    })

    const handleBack = () => {
        props.router.navigate(-1)
        setShowAreYouSureModal(false)
    }

    const handleModalClose = () => setShowAreYouSureModal(false)

    const handleConfirmSubmit = useCallback(async (
        industryEngagementId: string,
        values: IndustryEngagementFields,
        setSubmitting: (val: boolean) => any,
        reset: (e: any) => void
    ) => {
        setSubmitting(true)
        const res = await submitEditIndustryEngagementForm(industryEngagementId, values, () => reset({}))

        if (!res) {
            handleModalClose()
        } else {
            handleBack()
            const index = industryEngagements.findIndex((e: IndustryEngagement) => e.id === industryEngagementId)
            dispatch(setIndustryEngagement({
                industryEngagement: res,
                index
            }))
        }
        setSubmitting(false)
    }, [handleBack, handleModalClose])

    const statusOptions = useMemo(() => statuses.map((s: IndustryEngagementStatus) => ({
        value: s.id,
        label: s.description
    })), [statuses])

    const employeeOptions = useMemo(() => employees.map((e: Employee) => ({
        value: e.id,
        label: e.commonName ?? `${e.firstName} ${e.lastName}`
    })), [employees])

    const opportunityOptions = useMemo(() => opportunities.map((o: Opportunity) => ({
        value: o.id,
        label: o.description
    })), [opportunities])

    return <FormPageTemplate
        title={"Business Development"}
        breadcrumbItem={"Edit Industry Engagement"}
        actions={<ActionButton onClick={handleOpenManual}>Help <HelpIcon /></ActionButton>}
        form={<IndustryEngagementForm
            statuses={statusOptions}
            employees={employeeOptions}
            opportunities={opportunityOptions}
            values={values}
            errors={errors}
            touched={touched}
            handleChange={handleChange}
            handleBlur={handleBlur}
            handleSubmit={handleSubmit}
            handleCancel={handleBack}
            setFieldValue={setFieldValue}
            isValid={isValid}
            isSubmitting={isSubmitting}
            edit
        />}
        isLoading={isLoading}
        modal={<Modal
            isOpen={showAreYouSureModal}
            onClose={handleModalClose}
            title={"Confirm"}
            subtitle={"Are you sure you want to edit this industry engagement?"}
        >
            <AreYouSureButtons
                disable={isSubmitting}
                acceptButtonStyle={{ width: '150px' }}
                declineButtonStyle={{ width: '150px' }}
                handleAccept={() => handleConfirmSubmit(searchParams.get("industryEngagementId")!, values, setSubmitting, handleReset)}
                handleDecline={handleModalClose}
                acceptText={isSubmitting ? "Submitting..." : "Submit"}
                declineText={"Cancel"}
            />
        </Modal>}
    />
}

export default withRouter(EditIndustryEngagementPage)